import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Pencil, Plus, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  borrarRegAutoOperacion,
  crearRegAutoOperacion,
  descargarRegAutoPdf,
  editarRegAutoOperacion,
  getRegAuto,
  getRegAutoAutorizaciones,
  getRegAutoSugerencias,
  guardarRegAutoAutorizacion,
  type RegAutoOperacion,
  type RegAutoOperacionIn,
  type RegAutoSugerencia,
} from "@/lib/api";

/*
 * Registro Automatizado (SAT): las operaciones de importación que se llevan
 * por autorización aduanera, con el formato de los PDF "Registro Automatizado
 * – <cruce>" (banda ámbar + ocho columnas en romanos i–viii).
 *
 * Un cruce = un registro = un PDF. Los tres que existen (Ciudad Industrial
 * Laredo, Cumbres Frontera Railroad, Tapachula Los Brillantes) corresponden a
 * los enlaces LAA, RRD y GUA de la página de Importaciones y Exportaciones.
 *
 * El pedimento (i) y el medidor (v, vi) se capturan; el CFDI (ii, iii, iv) se
 * pre-llena eligiendo una factura de Facturas Recibidas; los porcentajes
 * (vii, viii) los calcula el backend y no se editan.
 */

const nf2 = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const nf3 = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 3 });

const money = (n: number | null | undefined, mon = "MXN") =>
  n == null ? "—" : new Intl.NumberFormat("es-MX", {
    style: "currency", currency: mon, maximumFractionDigits: 2,
  }).format(n);

const pct = (n: number | null) => (n == null ? "—" : `${nf2.format(n)} %`);

const VACIA: RegAutoOperacionIn = {
  id_autorizacion: 0, ejercicio: 0,
  pedimento_numero: "", pedimento_fecha: null, pedimento_clave: "",
  id_factura: null, cfdi_folio: "", cfdi_valor: null, cfdi_cantidad: null,
  medidor_lectura: null, medidor_fecha: null, notas: "",
};

export function RegistroAutomatizadoPage() {
  const qc = useQueryClient();
  const [idAut, setIdAut] = useState<number | null>(null);
  const [ejercicio, setEjercicio] = useState(new Date().getFullYear());
  const [editando, setEditando] = useState<RegAutoOperacionIn & { id?: number } | null>(null);
  const [editandoAut, setEditandoAut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const autsQ = useQuery({
    queryKey: ["reg-auto-autorizaciones"],
    queryFn: getRegAutoAutorizaciones,
  });
  const auts = autsQ.data?.items ?? [];

  useEffect(() => {
    if (idAut == null && auts.length > 0) setIdAut(auts[0].id_autorizacion);
  }, [auts, idAut]);

  const q = useQuery({
    queryKey: ["reg-auto", idAut, ejercicio],
    queryFn: () => getRegAuto(idAut!, ejercicio),
    enabled: idAut != null,
  });
  const reg = q.data;

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["reg-auto"] });
    qc.invalidateQueries({ queryKey: ["reg-auto-autorizaciones"] });
    qc.invalidateQueries({ queryKey: ["reg-auto-sugerencias"] });
  };

  const guardar = useMutation({
    mutationFn: (op: RegAutoOperacionIn & { id?: number }) =>
      op.id ? editarRegAutoOperacion(op.id, op) : crearRegAutoOperacion(op),
    onSuccess: () => { setEditando(null); setError(null); invalidar(); },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo guardar"),
  });

  const borrar = useMutation({
    mutationFn: (id: number) => borrarRegAutoOperacion(id),
    onSuccess: invalidar,
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo borrar"),
  });

  const items = reg?.items ?? [];
  const cols = reg?.columnas ?? autsQ.data?.columnas ?? [];
  const autSel = auts.find((a) => a.id_autorizacion === idAut);
  const faltaEncabezado = autSel && (!autSel.razon_social || !autSel.autorizacion);

  return (
    <div className="space-y-4">
      {/* ---------------------------------------------------------- encabezado */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-500" /> Registro Automatizado
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Operaciones de importación por autorización aduanera, en el formato del SAT.
            Un cruce = un registro. El <b>pedimento</b> y la <b>lectura del medidor</b> se
            capturan; el <b>CFDI</b> se toma de Facturas Recibidas y los <b>porcentajes de
            diferencia</b> se calculan solos.
          </p>
        </div>
        <button
          type="button"
          onClick={() =>
            idAut != null && descargarRegAutoPdf(idAut, ejercicio)
              .catch((e) => setError(e instanceof Error ? e.message : "No se pudo generar"))}
          disabled={idAut == null}
          className="inline-flex items-center gap-2 border text-slate-600 rounded px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
        >
          <FileText className="w-4 h-4" /> PDF del registro
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
      )}

      {/* ------------------------------------------------------ cruce + ejercicio */}
      <div className="bg-white border rounded-lg p-3 flex flex-wrap items-end gap-4">
        <div className="text-xs text-slate-500">
          Cruce aduanero
          <div className="flex flex-wrap gap-1 mt-1">
            {auts.map((a) => (
              <button
                key={a.id_autorizacion}
                type="button"
                onClick={() => setIdAut(a.id_autorizacion)}
                className={`rounded-full px-3 py-1 text-xs border ${
                  a.id_autorizacion === idAut
                    ? "bg-slate-800 text-white border-slate-800"
                    : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                {a.cruce}
                {a.punto && <span className="opacity-60"> · {a.punto}</span>}
              </button>
            ))}
          </div>
        </div>
        <label className="text-xs text-slate-500">
          Ejercicio fiscal
          <input
            type="number"
            value={ejercicio}
            min={2000}
            max={2100}
            onChange={(e) => setEjercicio(Number(e.target.value))}
            className="block border rounded px-2 py-1 text-sm text-slate-800 mt-0.5 w-24"
          />
        </label>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setEditandoAut(true)}
            disabled={autSel == null}
            className="inline-flex items-center gap-2 border text-slate-600 rounded px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <Pencil className="w-4 h-4" /> Datos del encabezado
          </button>
          <button
            type="button"
            onClick={() => setEditando({ ...VACIA, id_autorizacion: idAut!, ejercicio })}
            disabled={idAut == null}
            className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-3 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> Agregar operación
          </button>
        </div>
      </div>

      {faltaEncabezado && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
          Falta la <b>razón social</b> y/o el <b>número de autorización</b> de este cruce: en el
          encabezado y en el PDF aparecen como <b>XXXXX</b>, igual que en la plantilla. Captúralos
          en “Datos del encabezado”.
        </div>
      )}

      {q.isLoading && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">Cargando…</div>
      )}
      {q.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
          {(q.error as Error).message}
        </div>
      )}

      {/* -------------------------------------------- el formato, como el PDF */}
      {reg && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="text-center text-lg font-bold text-slate-900 py-2">
            Registro automatizado
          </div>
          <div className="bg-amber-400 border-y border-slate-400 text-center text-[11px] font-bold px-3 py-1.5 text-slate-900">
            {reg.encabezado}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  {cols.map((c) => (
                    <th
                      key={c.num}
                      className="border border-slate-300 px-2 py-2 text-left align-top font-semibold text-slate-700 min-w-[110px]"
                    >
                      {c.num}. {c.texto}
                    </th>
                  ))}
                  <th className="border border-slate-300 px-2 py-2 w-16" />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td
                      colSpan={cols.length + 1}
                      className="border border-slate-300 px-3 py-8 text-center text-slate-500"
                    >
                      Sin operaciones registradas en el ejercicio {ejercicio}.
                    </td>
                  </tr>
                )}
                {items.map((it) => (
                  <Fila
                    key={it.id_operacion}
                    it={it}
                    onEditar={() => setEditando({
                      id: it.id_operacion,
                      id_autorizacion: idAut!,
                      ejercicio,
                      pedimento_numero: it.pedimento_numero,
                      pedimento_fecha: it.pedimento_fecha,
                      pedimento_clave: it.pedimento_clave,
                      id_factura: it.id_factura,
                      cfdi_folio: it.cfdi_folio,
                      cfdi_valor: it.cfdi_valor,
                      cfdi_cantidad: it.cfdi_cantidad,
                      medidor_lectura: it.medidor_lectura,
                      medidor_fecha: it.medidor_fecha,
                      notas: it.notas,
                    })}
                    onBorrar={() => {
                      if (window.confirm(
                        `¿Borrar la operación del pedimento ${it.pedimento_numero || "(sin número)"}?`)) {
                        borrar.mutate(it.id_operacion);
                      }
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 border-t text-xs text-slate-500">
            {items.length} operación(es) en el ejercicio {ejercicio}
            {reg.sin_porcentaje.length > 0 && (
              <span className="text-amber-700">
                {" "}· {reg.sin_porcentaje.length} sin cantidad del CFDI o sin lectura del medidor,
                así que no entran al cálculo de los porcentajes
              </span>
            )}
            {" "}· los porcentajes (vii, viii) son (Σ cantidad CFDI − Σ lectura) ÷ Σ lectura del
            mes y del ejercicio
          </div>
        </div>
      )}

      {editando && (
        <ModalOperacion
          valor={editando}
          ejercicio={ejercicio}
          guardando={guardar.isPending}
          onCancelar={() => setEditando(null)}
          onGuardar={(op) => guardar.mutate(op)}
        />
      )}
      {editandoAut && autSel && (
        <ModalEncabezado
          cruce={autSel.cruce}
          razonSocial={autSel.razon_social}
          autorizacion={autSel.autorizacion}
          onCancelar={() => setEditandoAut(false)}
          onGuardar={async (rs, na) => {
            try {
              await guardarRegAutoAutorizacion(autSel.id_autorizacion,
                { razon_social: rs, autorizacion: na });
              setEditandoAut(false);
              setError(null);
              invalidar();
            } catch (e) {
              setError(e instanceof Error ? e.message : "No se pudo guardar");
            }
          }}
        />
      )}
    </div>
  );
}

function Fila({ it, onEditar, onBorrar }: {
  it: RegAutoOperacion; onEditar: () => void; onBorrar: () => void;
}) {
  const td = "border border-slate-300 px-2 py-1.5 align-top";
  const pedimento = [it.pedimento_numero, it.pedimento_fecha, it.pedimento_clave]
    .filter(Boolean).join(" · ");
  return (
    <tr className="hover:bg-slate-50">
      <td className={td}>{pedimento || <span className="text-slate-300">—</span>}</td>
      <td className={td}>
        {it.cfdi_folio || <span className="text-slate-300">—</span>}
        {it.emisor_nombre && (
          <div className="text-[10px] text-slate-400 truncate max-w-[160px]"
               title={it.emisor_nombre}>
            {it.emisor_nombre}
          </div>
        )}
      </td>
      <td className={`${td} text-right tabular-nums`}>{money(it.cfdi_valor)}</td>
      <td className={`${td} text-right tabular-nums`}>
        {it.cfdi_cantidad == null ? "—" : nf3.format(it.cfdi_cantidad)}
      </td>
      <td className={`${td} text-right tabular-nums`}>
        {it.medidor_lectura == null ? "—" : nf3.format(it.medidor_lectura)}
      </td>
      <td className={td}>{it.medidor_fecha ?? <span className="text-slate-300">—</span>}</td>
      <td className={`${td} text-right tabular-nums`}>{pct(it.pct_mensual)}</td>
      <td className={`${td} text-right tabular-nums`}>{pct(it.pct_anual)}</td>
      <td className={td}>
        <div className="flex items-center gap-2">
          <button type="button" title="Editar" onClick={onEditar}
                  className="text-slate-400 hover:text-blue-600">
            <Pencil className="w-4 h-4" />
          </button>
          <button type="button" title="Borrar" onClick={onBorrar}
                  className="text-slate-400 hover:text-red-600">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function Campo({ label, children, ayuda }: {
  label: string; children: React.ReactNode; ayuda?: string;
}) {
  return (
    <label className="block text-xs text-slate-500">
      {label}
      {children}
      {ayuda && <span className="block text-[10px] text-slate-400 mt-0.5">{ayuda}</span>}
    </label>
  );
}

const INPUT = "block w-full border rounded px-2 py-1 text-sm text-slate-800 mt-0.5";

function ModalOperacion({ valor, ejercicio, guardando, onCancelar, onGuardar }: {
  valor: RegAutoOperacionIn & { id?: number };
  ejercicio: number;
  guardando: boolean;
  onCancelar: () => void;
  onGuardar: (op: RegAutoOperacionIn & { id?: number }) => void;
}) {
  const [op, setOp] = useState(valor);
  const set = (k: keyof RegAutoOperacionIn, v: unknown) =>
    setOp((o) => ({ ...o, [k]: v }));

  const sugQ = useQuery({
    queryKey: ["reg-auto-sugerencias", ejercicio],
    queryFn: () => getRegAutoSugerencias(ejercicio),
  });
  const sugerencias = sugQ.data?.items ?? [];

  // Al elegir un CFDI se copian ii, iii y iv. Quedan editables: el registro es
  // un documento fiscal y manda lo que se declaró, no lo que hoy diga la factura.
  const tomarCfdi = (s: RegAutoSugerencia | null) => {
    if (!s) { set("id_factura", null); return; }
    setOp((o) => ({
      ...o,
      id_factura: s.id_factura,
      cfdi_folio: s.numero ?? "",
      cfdi_valor: s.total,
      cfdi_cantidad: s.cantidad || null,
    }));
  };

  const diferencia = useMemo(() => {
    const { cfdi_cantidad: c, medidor_lectura: l } = op;
    if (c == null || l == null || !l) return null;
    return ((c - l) / l) * 100;
  }, [op]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="font-semibold text-slate-800">
            {op.id ? "Editar operación" : "Agregar operación"}
          </h2>
          <button type="button" onClick={onCancelar} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <section>
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
              i. Pedimento
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Número">
                <input className={INPUT} value={op.pedimento_numero ?? ""}
                       onChange={(e) => set("pedimento_numero", e.target.value)} />
              </Campo>
              <Campo label="Fecha">
                <input type="date" className={INPUT} value={op.pedimento_fecha ?? ""}
                       onChange={(e) => set("pedimento_fecha", e.target.value || null)} />
              </Campo>
              <Campo label="Clave">
                <input className={INPUT} value={op.pedimento_clave ?? ""}
                       onChange={(e) => set("pedimento_clave", e.target.value)} />
              </Campo>
            </div>
          </section>

          <section>
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
              ii – iv. CFDI o documento equivalente
            </h3>
            <Campo
              label="Tomar de Facturas Recibidas"
              ayuda={sugQ.isLoading
                ? "Cargando facturas…"
                : `${sugerencias.length} CFDI del ${ejercicio} sin registrar. Al elegir uno se copian número, valor y cantidad.`}
            >
              <select
                className={INPUT}
                value={op.id_factura ?? ""}
                onChange={(e) => tomarCfdi(
                  sugerencias.find((s) => s.id_factura === Number(e.target.value)) ?? null)}
              >
                <option value="">— captura manual —</option>
                {sugerencias.map((s) => (
                  <option key={s.id_factura} value={s.id_factura}>
                    {s.numero} · {s.fecha?.slice(0, 10)} · {s.emisor_nombre ?? s.emisor_rfc}
                    {" · "}{money(s.total, s.moneda ?? "MXN")}
                  </option>
                ))}
              </select>
            </Campo>
            <div className="grid grid-cols-3 gap-3 mt-3">
              <Campo label="ii. Número del CFDI">
                <input className={INPUT} value={op.cfdi_folio ?? ""}
                       onChange={(e) => set("cfdi_folio", e.target.value)} />
              </Campo>
              <Campo label="iii. Valor">
                <input type="number" step="0.01" className={INPUT}
                       value={op.cfdi_valor ?? ""}
                       onChange={(e) => set("cfdi_valor",
                         e.target.value === "" ? null : Number(e.target.value))} />
              </Campo>
              <Campo label="iv. Cantidad amparada">
                <input type="number" step="0.001" className={INPUT}
                       value={op.cfdi_cantidad ?? ""}
                       onChange={(e) => set("cfdi_cantidad",
                         e.target.value === "" ? null : Number(e.target.value))} />
              </Campo>
            </div>
          </section>

          <section>
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
              v – vi. Medidor
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="v. Lectura del medidor">
                <input type="number" step="0.001" className={INPUT}
                       value={op.medidor_lectura ?? ""}
                       onChange={(e) => set("medidor_lectura",
                         e.target.value === "" ? null : Number(e.target.value))} />
              </Campo>
              <Campo label="vi. Fecha del reporte">
                <input type="date" className={INPUT} value={op.medidor_fecha ?? ""}
                       onChange={(e) => set("medidor_fecha", e.target.value || null)} />
              </Campo>
              <div className="text-xs text-slate-500">
                Diferencia de este renglón
                <div className={`text-lg font-semibold tabular-nums mt-0.5 ${
                  diferencia == null ? "text-slate-400"
                    : Math.abs(diferencia) > 5 ? "text-amber-600" : "text-slate-800"}`}>
                  {diferencia == null ? "—" : `${nf2.format(diferencia)} %`}
                </div>
                <span className="block text-[10px] text-slate-400">
                  (iv − v) ÷ v · los de vii y viii son del mes y del ejercicio
                </span>
              </div>
            </div>
          </section>

          <Campo label="Notas (no salen en el formato)">
            <textarea className={INPUT} rows={2} value={op.notas ?? ""}
                      onChange={(e) => set("notas", e.target.value)} />
          </Campo>
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t bg-slate-50">
          <button type="button" onClick={onCancelar}
                  className="border rounded px-3 py-1.5 text-sm text-slate-600 hover:bg-white">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onGuardar(op)}
            disabled={guardando}
            className="bg-blue-600 text-white rounded px-4 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {guardando ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalEncabezado({ cruce, razonSocial, autorizacion, onCancelar, onGuardar }: {
  cruce: string;
  razonSocial: string | null;
  autorizacion: string | null;
  onCancelar: () => void;
  onGuardar: (razonSocial: string, autorizacion: string) => void;
}) {
  const [rs, setRs] = useState(razonSocial ?? "");
  const [na, setNa] = useState(autorizacion ?? "");
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="font-semibold text-slate-800">Datos del encabezado · {cruce}</h2>
          <button type="button" onClick={onCancelar} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs text-slate-500">
            Son los dos <b>XXXXX</b> de la plantilla. Aplican a todos los ejercicios de este cruce.
          </p>
          <Campo label="Razón social">
            <input className={INPUT} value={rs} onChange={(e) => setRs(e.target.value)} />
          </Campo>
          <Campo label="Número de autorización">
            <input className={INPUT} value={na} onChange={(e) => setNa(e.target.value)} />
          </Campo>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t bg-slate-50">
          <button type="button" onClick={onCancelar}
                  className="border rounded px-3 py-1.5 text-sm text-slate-600 hover:bg-white">
            Cancelar
          </button>
          <button type="button" onClick={() => onGuardar(rs, na)}
                  className="bg-blue-600 text-white rounded px-4 py-1.5 text-sm hover:bg-blue-700">
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
